import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, ArrowUpRight, ArrowDownRight, Wallet, Loader2, Download, TrendingUp, TrendingDown, Star, Bell, Clock, X } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mini sparkline component with gradient fill
const Sparkline = ({ data, width = 100, height = 30 }) => {
  if (!data || data.length < 2) return <div style={{ width, height }} className="bg-secondary/10 rounded" />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`);
  const isUp = data[data.length - 1] >= data[0];
  const c = isUp ? '#10B981' : '#EF4444';
  const fillPts = [...pts, `${width},${height}`, `0,${height}`].join(' ');
  const uid = `sp_${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg width={width} height={height} className="inline-block">
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.25" />
          <stop offset="100%" stopColor={c} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPts} fill={`url(#${uid})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={c} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

// Price cell with flash effect
const PriceCell = ({ price, prevPrice, fmt }) => {
  const diff = prevPrice ? price - prevPrice : 0;
  const flash = Math.abs(diff) > 0.001
    ? (diff > 0 ? 'animate-flash-green' : 'animate-flash-red')
    : '';
  const color = Math.abs(diff) > 0.001
    ? (diff > 0 ? 'text-emerald-400' : 'text-red-400')
    : '';
  return (
    <span className={`font-mono text-[13px] tabular-nums inline-block px-1.5 py-0.5 rounded ${flash} ${color}`} key={prevPrice}>
      {fmt(price)} €
    </span>
  );
};

export default function ClientCryptoBuy() {
  const { token, user } = useAuth();
  const { primaryColor } = useTheme();
  const [prices, setPrices] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [prevLivePrices, setPrevLivePrices] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFavOnly, setShowFavOnly] = useState(false);

  // Buy dialog
  const [buyDialog, setBuyDialog] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [buyAmount, setBuyAmount] = useState('');
  const [buyMode, setBuyMode] = useState('eur'); // 'eur' or 'crypto'
  const [buying, setBuying] = useState(false);
  const [available, setAvailable] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [chartDays, setChartDays] = useState(7);
  const [loadingChart, setLoadingChart] = useState(false);

  // Alert dialog
  const [alertDialog, setAlertDialog] = useState(false);
  const [alertCoin, setAlertCoin] = useState(null);
  const [alertCondition, setAlertCondition] = useState('below');
  const [alertPrice, setAlertPrice] = useState('');

  // Order dialog
  const [orderDialog, setOrderDialog] = useState(false);
  const [orderCoin, setOrderCoin] = useState(null);
  const [orderType, setOrderType] = useState('buy');
  const [orderPrice, setOrderPrice] = useState('');
  const [orderAmount, setOrderAmount] = useState('');

  // Receive
  const [wallets, setWallets] = useState({});
  const [receiveDialog, setReceiveDialog] = useState(false);
  const [receiveCoin, setReceiveCoin] = useState(null);

  useEffect(() => { 
    fetchAll(); 
    const i = setInterval(fetchAll, 120000); 
    const timeout = setTimeout(() => setLoading(false), 5000); // Force stop loading after 5s
    return () => { clearInterval(i); clearTimeout(timeout); };
  }, [token]);

  useEffect(() => {
    if (!prices.length) return;
    setLivePrices(prev => {
      const n = { ...prev };
      prices.forEach(c => { if (c.current_price > 0 && !n[c.symbol]) n[c.symbol] = c.current_price; });
      return n;
    });
    const tick = setInterval(() => {
      setPrevLivePrices(prev => ({ ...livePrices }));
      setLivePrices(prev => {
        const n = { ...prev };
        prices.forEach(c => {
          const b = prev[c.symbol] || c.current_price;
          if (b > 0) n[c.symbol] = b + b * (Math.random() - 0.5) * 0.001;
        });
        return n;
      });
    }, 2000);
    return () => clearInterval(tick);
  }, [prices]);

  const fetchAll = async () => {
    try {
      const [pricesRes, balRes, walRes, favRes, alertsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/crypto/prices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/portfolios`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/wallet-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/crypto/favorites`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/alerts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/orders`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      const np = pricesRes.data || [];
      setPrices(np);
      setAvailable((balRes.data || []).filter(p => p.portfolio_type === 'available').reduce((s, p) => s + (p.available || 0), 0));
      setWallets(walRes.data || {});
      setFavorites(favRes.data || []);
      setAlerts(alertsRes.data || []);
      setOrders(ordersRes.data || []);
      setLivePrices(prev => { const n = { ...prev }; np.forEach(c => { if (c.current_price > 0) n[c.symbol] = c.current_price; }); return n; });
    } catch {} finally { setLoading(false); }
  };

  const fetchChart = async (coinId, days) => {
    setLoadingChart(true);
    try { const r = await axios.get(`${API}/crypto/chart/${coinId}?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }); setChartData((r.data?.prices || []).map(([ts, p]) => ({ time: new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }), price: p }))); }
    catch { setChartData([]); } finally { setLoadingChart(false); }
  };

  const getLive = (s) => livePrices[s] || 0;
  const fmt = (v, d = 2) => !v ? '0' : v >= 1 ? v.toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }) : v.toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 8 });

  const filtered = useMemo(() => {
    let list = prices;
    if (showFavOnly) list = list.filter(c => favorites.includes(c.symbol));
    if (search) { const s = search.toLowerCase(); list = list.filter(c => c.symbol.toLowerCase().includes(s) || c.name.toLowerCase().includes(s)); }
    return list;
  }, [prices, search, showFavOnly, favorites]);

  const toggleFav = async (symbol) => {
    try { const r = await axios.post(`${API}/crypto/favorites`, { symbol }, { headers: { Authorization: `Bearer ${token}` } }); setFavorites(r.data.favorites); }
    catch {}
  };

  const openBuy = (coin) => { setSelectedCoin(coin); setBuyAmount(''); setBuyMode('eur'); setChartDays(7); setBuyDialog(true); fetchChart(coin.id, 7); };
  const handleBuy = async () => {
    const val = parseFloat(buyAmount);
    if (!val || val <= 0) { toast.error('Montant invalide'); return; }
    const amtEur = buyMode === 'eur' ? val : val * buyLivePrice;
    if (amtEur > available) { toast.error('Fonds insuffisants'); return; }
    setBuying(true);
    try { const r = await axios.post(`${API}/crypto/buy`, { symbol: selectedCoin.symbol, amount_eur: amtEur }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`${r.data.quantity?.toFixed(8)} ${selectedCoin.symbol} acheté !`); setBuyDialog(false); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } finally { setBuying(false); }
  };

  const handleCreateAlert = async () => {
    if (!alertPrice || !alertCoin) return;
    try { await axios.post(`${API}/crypto/alerts`, { symbol: alertCoin.symbol, condition: alertCondition, target_price: parseFloat(alertPrice) }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Alerte créée !'); setAlertDialog(false); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateOrder = async () => {
    if (!orderPrice || !orderAmount || !orderCoin) return;
    try { await axios.post(`${API}/crypto/orders`, { symbol: orderCoin.symbol, order_type: orderType, target_price: parseFloat(orderPrice), amount_eur: orderType === 'buy' ? parseFloat(orderAmount) : 0, quantity: orderType === 'sell' ? parseFloat(orderAmount) : 0 }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Ordre créé !'); setOrderDialog(false); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const getWallet = (s) => wallets[s] || null;
  const openReceive = (c) => { setReceiveCoin(c); setReceiveDialog(true); };
  const handleRequestWallet = async () => { if (!receiveCoin) return; try { await axios.post(`${API}/crypto/wallet-request`, { symbol: receiveCoin.symbol }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Demande envoyée !'); fetchAll(); } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } };
  const copyAddr = (a) => { navigator.clipboard.writeText(a); toast.success('Copié !'); };

  const buyLivePrice = selectedCoin ? getLive(selectedCoin.symbol) : 0;
  const buyEurAmount = buyMode === 'eur' ? (parseFloat(buyAmount) || 0) : (parseFloat(buyAmount) || 0) * buyLivePrice;
  const buyCryptoQty = buyMode === 'crypto' ? (parseFloat(buyAmount) || 0) : (buyLivePrice > 0 ? (parseFloat(buyAmount) || 0) / buyLivePrice : 0);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="spinner" /></div></DashboardLayout>;

  const PctCell = ({ val }) => { const up = val >= 0; return <span className={`flex items-center justify-end gap-0.5 font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>{up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{Math.abs(val).toFixed(2)}%</span>; };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="crypto-buy-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Marché Crypto</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span></span>
              <p className="text-sm text-muted-foreground">{prices.length} actifs · <span className="text-emerald-400">En direct</span></p>
            </div>
          </div>
          <Badge variant="outline" className="text-sm py-1.5 px-4 border-primary/30"><Wallet className="w-4 h-4 mr-2" />{available.toLocaleString('fr-FR')} €</Badge>
        </div>

        {/* Pending orders & alerts */}
        {(orders.length > 0 || alerts.length > 0) && (
          <div className="flex flex-wrap gap-2">
            {orders.map(o => (
              <Badge key={o.id} variant="outline" className="text-xs py-1 px-2 border-yellow-500/30 bg-yellow-500/5">
                <Clock className="w-3 h-3 mr-1 text-yellow-400" />
                {o.order_type === 'buy' ? 'Achat' : 'Vente'} {o.symbol} @ {fmt(o.target_price)} €
                <button onClick={async () => { await axios.delete(`${API}/crypto/orders/${o.id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchAll(); }} className="ml-1.5 hover:text-red-400"><X className="w-3 h-3" /></button>
              </Badge>
            ))}
            {alerts.map(a => (
              <Badge key={a.id} variant="outline" className="text-xs py-1 px-2 border-blue-500/30 bg-blue-500/5">
                <Bell className="w-3 h-3 mr-1 text-blue-400" />
                {a.symbol} {a.condition === 'below' ? '<' : '>'} {fmt(a.target_price)} €
                <button onClick={async () => { await axios.delete(`${API}/crypto/alerts/${a.id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchAll(); }} className="ml-1.5 hover:text-red-400"><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-9" data-testid="crypto-search" />
          </div>
          <Button size="sm" variant={showFavOnly ? 'default' : 'outline'} onClick={() => setShowFavOnly(!showFavOnly)} className="h-9">
            <Star className={`w-4 h-4 mr-1 ${showFavOnly ? 'fill-current' : ''}`} />Favoris
          </Button>
        </div>

        <Card className="bg-card/80 backdrop-blur border-border/30">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/30 text-[10px] text-muted-foreground uppercase tracking-wider">
                    <th className="py-2.5 px-2 w-6"></th>
                    <th className="text-left py-2.5 px-2 w-8">#</th>
                    <th className="text-left py-2.5 px-2">Actif</th>
                    <th className="text-right py-2.5 px-2">Prix</th>
                    <th className="text-right py-2.5 px-2">1h</th>
                    <th className="text-right py-2.5 px-2">24h</th>
                    <th className="text-right py-2.5 px-2">7j</th>
                    <th className="text-center py-2.5 px-2 hidden md:table-cell w-[110px]">7j</th>
                    <th className="text-right py-2.5 px-2 hidden lg:table-cell">Cap.</th>
                    <th className="text-right py-2.5 px-2 w-[200px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((coin, idx) => {
                    const lp = getLive(coin.symbol);
                    const isFav = favorites.includes(coin.symbol);
                    return (
                      <tr key={coin.id} className="border-b border-border/10 hover:bg-white/[0.02] transition-colors" data-testid={`crypto-row-${coin.symbol}`}>
                        <td className="py-2 px-2"><button onClick={() => toggleFav(coin.symbol)} className={`transition-colors ${isFav ? 'text-yellow-400' : 'text-muted-foreground/30 hover:text-yellow-400'}`}><Star className={`w-3.5 h-3.5 ${isFav ? 'fill-current' : ''}`} /></button></td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">{coin.market_cap_rank || idx + 1}</td>
                        <td className="py-2 px-2 cursor-pointer" onClick={() => openBuy(coin)}>
                          <div className="flex items-center gap-2.5">
                            {coin.image && <img src={coin.image} alt="" className="w-6 h-6 rounded-full" onError={e => { e.target.style.display = 'none'; }} />}
                            <div><span className="font-semibold text-sm">{coin.symbol}</span><span className="text-[10px] text-muted-foreground ml-1.5 hidden sm:inline">{coin.name}</span></div>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right cursor-pointer" onClick={() => openBuy(coin)}>
                          <PriceCell price={lp} prevPrice={prevLivePrices[coin.symbol]} fmt={fmt} />
                        </td>
                        <td className="py-2 px-2 text-right text-xs"><PctCell val={coin.price_change_percentage_1h || 0} /></td>
                        <td className="py-2 px-2 text-right text-xs"><PctCell val={coin.price_change_percentage_24h || 0} /></td>
                        <td className="py-2 px-2 text-right text-xs"><PctCell val={coin.price_change_percentage_7d || 0} /></td>
                        <td className="py-2 px-2 text-center hidden md:table-cell"><Sparkline data={coin.sparkline} /></td>
                        <td className="py-2 px-2 text-right text-muted-foreground text-xs hidden lg:table-cell">{coin.market_cap ? `${(coin.market_cap / 1e9).toFixed(1)}B` : '-'}</td>
                        <td className="py-2 px-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" className="h-6 text-[10px] px-2" style={{ backgroundColor: primaryColor }} onClick={() => openBuy(coin)} data-testid={`buy-${coin.symbol}`}>Acheter</Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setAlertCoin(coin); setAlertPrice(''); setAlertDialog(true); }} title="Alerte prix"><Bell className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setOrderCoin(coin); setOrderPrice(''); setOrderAmount(''); setOrderDialog(true); }} title="Ordre limite"><Clock className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openReceive(coin)}><Download className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Buy Dialog */}
        <Dialog open={buyDialog} onOpenChange={setBuyDialog}>
          <DialogContent className="max-w-xl" data-testid="buy-crypto-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-3">{selectedCoin?.image && <img src={selectedCoin.image} alt="" className="w-9 h-9 rounded-full" />}<div><span className="text-lg">{selectedCoin?.name}</span><span className="text-sm text-muted-foreground ml-2">{selectedCoin?.symbol}</span></div></DialogTitle></DialogHeader>
            {selectedCoin && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30">
                  <div><p className="text-[10px] text-muted-foreground uppercase">Prix en direct</p><p className="text-3xl font-bold font-mono transition-all">{fmt(buyLivePrice)} €</p></div>
                  <div className="text-right space-y-0.5">
                    {[{ l: '1h', v: selectedCoin.price_change_percentage_1h }, { l: '24h', v: selectedCoin.price_change_percentage_24h }, { l: '7j', v: selectedCoin.price_change_percentage_7d }].map(p => (<div key={p.l} className={`text-xs font-medium ${(p.v || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{p.l}: {(p.v || 0) >= 0 ? '+' : ''}{(p.v || 0).toFixed(2)}%</div>))}
                    <p className="text-[9px] text-muted-foreground animate-pulse mt-1">EN DIRECT</p>
                  </div>
                </div>
                <div>
                  <div className="flex gap-1 mb-1.5">{[{ d: 1, l: '24h' }, { d: 7, l: '7j' }, { d: 30, l: '30j' }, { d: 90, l: '90j' }].map(o => (<Button key={o.d} size="sm" variant={chartDays === o.d ? 'default' : 'ghost'} className="text-xs h-6 px-2" onClick={() => { setChartDays(o.d); fetchChart(selectedCoin.id, o.d); }}>{o.l}</Button>))}</div>
                  <div className="h-36 w-full rounded-lg bg-secondary/10 overflow-hidden">
                    {loadingChart ? <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                      : chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%"><AreaChart data={chartData}><defs><linearGradient id="cG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={primaryColor || '#D4AF37'} stopOpacity={0.3} /><stop offset="95%" stopColor={primaryColor || '#D4AF37'} stopOpacity={0} /></linearGradient></defs><XAxis dataKey="time" hide /><YAxis domain={['auto', 'auto']} hide /><Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} formatter={v => [`${v.toLocaleString('fr-FR')} €`, 'Prix']} /><Area type="monotone" dataKey="price" stroke={primaryColor || '#D4AF37'} fill="url(#cG)" strokeWidth={2} dot={false} /></AreaChart></ResponsiveContainer>
                      ) : <div className="flex items-center justify-center h-full text-muted-foreground text-xs"><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Chargement...</div>}
                  </div>
                </div>
                <div className="pt-2 border-t border-border/20">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-muted-foreground">Montant</Label>
                    <div className="flex gap-1">
                      <Button size="sm" variant={buyMode === 'eur' ? 'default' : 'outline'} onClick={() => { setBuyMode('eur'); setBuyAmount(''); }} className="text-xs h-7 px-3">EUR €</Button>
                      <Button size="sm" variant={buyMode === 'crypto' ? 'default' : 'outline'} onClick={() => { setBuyMode('crypto'); setBuyAmount(''); }} className="text-xs h-7 px-3">{selectedCoin?.symbol}</Button>
                    </div>
                  </div>
                  <Input type="number" step="any" value={buyAmount} onChange={e => setBuyAmount(e.target.value)} placeholder={buyMode === 'eur' ? '100.00' : '0.001'} className="text-lg font-mono h-11" autoFocus data-testid="buy-amount-input" />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-muted-foreground">Solde: {available.toLocaleString('fr-FR')} €</p>
                    {buyMode === 'eur' && <div className="flex gap-1">{[25, 50, 100, 250, 500].map(a => (<button key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/50 hover:bg-secondary" onClick={() => setBuyAmount(String(a))}>{a}€</button>))}</div>}
                  </div>
                </div>
                {buyAmount && parseFloat(buyAmount) > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Vous recevrez</span>
                      <span className="font-mono font-bold text-emerald-400 transition-all">{buyCryptoQty.toFixed(8)} {selectedCoin.symbol}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Coût total</span>
                      <span className="font-mono">{buyEurAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>@ {fmt(buyLivePrice)} € / {selectedCoin.symbol}</span>
                      <span className="italic">prix indicatif</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setBuyDialog(false)}>Annuler</Button><Button onClick={handleBuy} disabled={buying || !buyAmount || parseFloat(buyAmount) <= 0 || buyEurAmount > available} className="min-w-[140px]" style={{ backgroundColor: primaryColor }} data-testid="confirm-buy-btn">{buying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Achat...</> : `Acheter ${selectedCoin?.symbol}`}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Alert Dialog */}
        <Dialog open={alertDialog} onOpenChange={setAlertDialog}>
          <DialogContent className="max-w-sm" data-testid="alert-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5" />Alerte prix {alertCoin?.symbol}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Prix actuel: <span className="font-mono font-bold">{alertCoin ? fmt(getLive(alertCoin.symbol)) : 0} €</span></p>
              <div><Label>Me notifier quand le prix</Label>
                <Select value={alertCondition} onValueChange={setAlertCondition}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="below">Descend sous</SelectItem><SelectItem value="above">Monte au-dessus de</SelectItem></SelectContent></Select>
              </div>
              <div><Label>Prix cible (€)</Label><Input type="number" step="0.01" value={alertPrice} onChange={e => setAlertPrice(e.target.value)} placeholder="55000" data-testid="alert-price-input" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setAlertDialog(false)}>Annuler</Button><Button onClick={handleCreateAlert} style={{ backgroundColor: primaryColor }}>Créer l'alerte</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Order Dialog */}
        <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
          <DialogContent className="max-w-sm" data-testid="order-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />Ordre limite {orderCoin?.symbol}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Prix actuel: <span className="font-mono font-bold">{orderCoin ? fmt(getLive(orderCoin.symbol)) : 0} €</span></p>
              <div><Label>Type d'ordre</Label>
                <Select value={orderType} onValueChange={setOrderType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="buy">Acheter quand le prix descend à</SelectItem><SelectItem value="sell">Vendre quand le prix monte à</SelectItem></SelectContent></Select>
              </div>
              <div><Label>Prix cible (€)</Label><Input type="number" step="0.01" value={orderPrice} onChange={e => setOrderPrice(e.target.value)} placeholder="55000" data-testid="order-price-input" /></div>
              <div><Label>{orderType === 'buy' ? 'Montant (€)' : `Quantité (${orderCoin?.symbol})`}</Label><Input type="number" step="any" value={orderAmount} onChange={e => setOrderAmount(e.target.value)} placeholder={orderType === 'buy' ? '100' : '0.001'} data-testid="order-amount-input" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOrderDialog(false)}>Annuler</Button><Button onClick={handleCreateOrder} style={{ backgroundColor: primaryColor }}>Créer l'ordre</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receive Dialog */}
        <Dialog open={receiveDialog} onOpenChange={setReceiveDialog}>
          <DialogContent className="max-w-md" data-testid="receive-dialog-buy">
            <DialogHeader><DialogTitle className="flex items-center gap-2">{receiveCoin?.image && <img src={receiveCoin.image} alt="" className="w-6 h-6 rounded-full" />}Recevoir {receiveCoin?.symbol}</DialogTitle></DialogHeader>
            {receiveCoin && (() => { const w = getWallet(receiveCoin.symbol); if (!w) return <div className="space-y-4 text-center py-4"><Wallet className="w-12 h-12 mx-auto text-primary opacity-50" /><p className="text-sm text-muted-foreground">Pas encore de wallet.</p><Button onClick={handleRequestWallet} style={{ backgroundColor: primaryColor }} className="w-full">Demander un wallet</Button></div>; if (w.status === 'pending') return <div className="text-center py-4"><Badge className="bg-yellow-500/20 text-yellow-400">En attente</Badge><p className="text-sm text-muted-foreground mt-2">Demande en cours.</p></div>; return <div className="space-y-4"><Badge className="bg-emerald-500/20 text-emerald-400">Actif</Badge>{w.qr_code_url && <div className="flex justify-center py-2"><img src={w.qr_code_url} alt="QR Code" className="w-48 h-48 bg-white rounded-lg p-2 object-contain" /></div>}<div className="p-4 rounded-lg bg-secondary/30"><p className="font-mono text-sm break-all select-all">{w.wallet_address}</p>{w.network && <p className="text-xs text-muted-foreground mt-1">Réseau: {w.network}</p>}</div><Button variant="outline" className="w-full" onClick={() => copyAddr(w.wallet_address)}>Copier</Button></div>; })()}
            <DialogFooter><Button variant="outline" onClick={() => setReceiveDialog(false)}>Fermer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
